
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertCircle, Check, Plus, Search, Workflow, ArrowRight, Calendar, Activity } from 'lucide-react';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/auth/AuthContext';
import { WorkflowEditor } from '@/components/agentic/workflows';
import { 
  fetchWorkflows, 
  createWorkflow, 
  executeWorkflow 
} from '@/services/agentic/workflowService';
import { AgentWorkflow, workflow_trigger_type } from '@/types/agentic';
import { Node, Edge } from '@xyflow/react';

const WorkflowsPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('all');
  const [workflows, setWorkflows] = useState<AgentWorkflow[]>([]);
  const [filteredWorkflows, setFilteredWorkflows] = useState<AgentWorkflow[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreatingWorkflow, setIsCreatingWorkflow] = useState(false);
  const [newWorkflowName, setNewWorkflowName] = useState('');
  const [newWorkflowDescription, setNewWorkflowDescription] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loadWorkflows = async () => {
    setIsLoading(true);
    try {
      const workflowsData = await fetchWorkflows();
      setWorkflows(workflowsData);
      setFilteredWorkflows(workflowsData);
    } catch (error) {
      console.error('Error loading workflows:', error);
      toast.error('Failed to load workflows');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadWorkflows();
  }, []);

  useEffect(() => {
    const filtered = workflows.filter(
      (workflow) =>
        workflow.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (workflow.description?.toLowerCase() || '').includes(searchQuery.toLowerCase())
    );
    setFilteredWorkflows(filtered);
  }, [searchQuery, workflows]);

  useEffect(() => {
    if (activeTab === 'all') {
      setFilteredWorkflows(workflows);
    } else if (activeTab === 'active') {
      setFilteredWorkflows(workflows.filter((workflow) => workflow.is_active));
    } else if (activeTab === 'inactive') {
      setFilteredWorkflows(workflows.filter((workflow) => !workflow.is_active));
    }
  }, [activeTab, workflows]);

  const handleCreateWorkflow = async () => {
    if (!newWorkflowName.trim()) {
      toast.error('Please enter a workflow name');
      return;
    }

    setIsSubmitting(true);

    try {
      const newWorkflow = await createWorkflow({
        name: newWorkflowName,
        description: newWorkflowDescription,
        created_by: user?.id || '',
        user_id: user?.id || '', // Added user_id field
        canvas_data: { nodes: [], edges: [] },
        is_active: false,
        trigger_type: 'manual' as workflow_trigger_type,
        trigger_config: {}
      });

      setWorkflows((prev) => [...prev, newWorkflow]);
      setNewWorkflowName('');
      setNewWorkflowDescription('');
      setIsCreatingWorkflow(false);
      
      navigate(`/agentic/workflows/edit/${newWorkflow.id}`);
      
      toast.success('Workflow created successfully');
    } catch (error) {
      console.error('Error creating workflow:', error);
      toast.error('Failed to create workflow');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleExecuteWorkflow = async (workflowId: string) => {
    try {
      await executeWorkflow(workflowId);
      toast.success('Workflow execution started');
    } catch (error) {
      console.error('Error executing workflow:', error);
      toast.error('Failed to execute workflow');
    }
  };

  // The rest of the component with the return statement
  return (
    <div className="container mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Workflow Management</h1>
        <Button onClick={() => setIsCreatingWorkflow(true)} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Create Workflow
        </Button>
      </div>

      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search workflows..."
            className="pl-8"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full md:w-auto">
          <TabsList>
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="active">Active</TabsTrigger>
            <TabsTrigger value="inactive">Inactive</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Workflow className="h-5 w-5 text-primary" />
              Workflows
            </CardTitle>
            <CardDescription>
              Manage and execute your automated workflow processes
            </CardDescription>
          </CardHeader>
          <CardContent>
            {filteredWorkflows.length === 0 ? (
              <div className="text-center py-8">
                <Workflow className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                <h3 className="text-lg font-medium">No workflows found</h3>
                <p className="text-muted-foreground mt-1">
                  {searchQuery
                    ? "No workflows match your search criteria"
                    : "Create your first workflow to get started"}
                </p>
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => setIsCreatingWorkflow(true)}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Workflow
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Trigger Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Last Modified</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredWorkflows.map((workflow) => (
                    <TableRow key={workflow.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center">
                          <Workflow className="h-4 w-4 mr-2 text-primary" />
                          {workflow.name}
                        </div>
                        {workflow.description && (
                          <div className="text-xs text-muted-foreground mt-1">
                            {workflow.description}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {workflow.trigger_type === 'manual' && (
                          <div className="flex items-center gap-1">
                            <Activity className="h-3 w-3" />
                            <span>Manual</span>
                          </div>
                        )}
                        {workflow.trigger_type === 'scheduled' && (
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            <span>Scheduled</span>
                          </div>
                        )}
                        {workflow.trigger_type === 'event' && (
                          <div className="flex items-center gap-1">
                            <AlertCircle className="h-3 w-3" />
                            <span>Event</span>
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {workflow.is_active ? (
                          <div className="flex items-center">
                            <div className="h-2 w-2 rounded-full bg-green-500 mr-2"></div>
                            <span>Active</span>
                          </div>
                        ) : (
                          <div className="flex items-center">
                            <div className="h-2 w-2 rounded-full bg-gray-300 mr-2"></div>
                            <span>Inactive</span>
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {new Date(workflow.updated_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost" 
                            size="sm"
                            onClick={() => navigate(`/agentic/workflows/edit/${workflow.id}`)}
                          >
                            Edit
                          </Button>
                          {workflow.is_active && (
                            <Button
                              variant="outline" 
                              size="sm"
                              onClick={() => handleExecuteWorkflow(workflow.id)}
                            >
                              Execute
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      <Dialog open={isCreatingWorkflow} onOpenChange={setIsCreatingWorkflow}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Workflow</DialogTitle>
            <DialogDescription>
              Create a new automated workflow for your agent capabilities.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="workflow-name">Workflow Name</Label>
              <Input
                id="workflow-name"
                placeholder="Enter workflow name"
                value={newWorkflowName}
                onChange={(e) => setNewWorkflowName(e.target.value)}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="workflow-description">Description (Optional)</Label>
              <Input
                id="workflow-description"
                placeholder="Enter workflow description"
                value={newWorkflowDescription}
                onChange={(e) => setNewWorkflowDescription(e.target.value)}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setIsCreatingWorkflow(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleCreateWorkflow}
              disabled={isSubmitting || !newWorkflowName.trim()}
            >
              {isSubmitting ? (
                <span className="flex items-center gap-1">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"></span>
                  Creating...
                </span>
              ) : (
                <span className="flex items-center gap-1">
                  <Plus className="h-4 w-4" />
                  Create
                </span>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WorkflowsPage;
